import React, { useEffect, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from '../toasts';
import { FaPlus } from 'react-icons/fa';
import { FaUsers } from 'react-icons/fa';
import { getError, getUserInfo } from '../utils';
import ObjectTable from '../components/ObjectTable/ObjectTable';
import UserService from '../services/user.service';
import ConfirmationModal from '../components/ConfirmationModal/ConfirmationModal';
import { useLabels } from '../hooks/useLabels';

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_SUCCESS':
		return { ...state, users: action.payload, loading: false };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'DELETE_REQUEST':
		return { ...state, loadingDelete: true, successDelete: false };
	case 'DELETE_SUCCESS':
		return {
			...state,
			loadingDelete: false,
			successDelete: true,
		};
	case 'DELETE_FAIL':
		return { ...state, loadingDelete: false, successDelete: false };
	case 'DELETE_RESET':
		return { ...state, loadingDelete: false, successDelete: false };
	default:
		return state;
	}
};

const usersColumns = [
	{ customId: { type: 'string', label: 'ID' } },
	{ name: { type: 'string', label: 'Nombre' } },
	{ email: { type: 'string', label: 'Email' } },
	{ isAdmin: { type: 'boolean', label: 'Es Administrador?' } },
	{ role: { type: 'string', label: 'Rol' } },
];

function AdminUsers() {
	const { t } = useLabels();
	const navigate = useNavigate();
	const [{ loading, error, users, successDelete }, dispatch] = useReducer(reducer, {
		users: [],
		loading: true,
		error: '',
	});

	const [isOpen, setIsOpen] = useState(false);
	const [filterOptions, setFilterOptions] = useState({
		formType: 'Usuarios',
		query: 'all',
	});
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [userToDelete, setUserToDelete] = useState(null);

	useEffect(() => {
		const userInfo = getUserInfo();
		const isAdmin = userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin');
		if (!isAdmin) {
			navigate('/AdminScreen');
			return;
		}
	}, [navigate]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				dispatch({ type: 'FETCH_REQUEST' });
				const { data } = await UserService.list();
				// Map users data to include customId and ensure role is set
				const mappedData = Array.isArray(data) ? data.map(user => ({
					...user,
					customId: user._id,
					role: user.role || 'employee'
				})) : [];
				dispatch({ type: 'FETCH_SUCCESS', payload: mappedData });
			} catch (err) {
				dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
			}
		};
		const userInfo = getUserInfo();
		const isAdmin = userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin');
		if (isAdmin) {
			if (successDelete) {
				dispatch({ type: 'DELETE_RESET' });
			} else {
				fetchData();
			}
		}
	}, [successDelete]);

	function deleteUserHandler(user) {
		setUserToDelete(user);
		setShowDeleteModal(true);
	}

	async function handleConfirmDelete() {
		if (userToDelete) {
			try {
				dispatch({ type: 'DELETE_REQUEST' });
				await UserService.delete(userToDelete._id);
				dispatch({ type: 'DELETE_SUCCESS' });
				toast.success('Usuario Eliminado');
			} catch (err) {
				toast.error(getError(err));
				dispatch({ type: 'DELETE_FAIL' });
			}
		}
	}

	function handleCloseDeleteModal() {
		setShowDeleteModal(false);
		setUserToDelete(null);
	}

	function editUserHandler(user) {
		navigate(`/AdminScreen/users/editUser/${user._id}`);
	}

	function seeDetailsHandler(user) {
		navigate(`/AdminScreen/users/editUser/${user._id}`);
	}

	function newUserHandler() {
		navigate('/AdminScreen/newUser');
	}

	// eslint-disable-next-line no-unused-vars
	function getFilterUrl(filter) {
		return '/AdminScreen/AdminUsers';
	}

	function handleDropdownToggle() {
		setIsOpen(!isOpen);
	}

	function handleInputChange(e) {
		const { name, value } = e.target;
		setFilterOptions((prevOptions) => ({
			...prevOptions,
			[name]: value,
		}));
	}

	function handleClearFilter() {
		setFilterOptions({
			formType: 'Usuarios',
			query: 'all',
		});
	}

	function handleApplyFilter() {
		// No filter implementation needed for now
	}

	// eslint-disable-next-line no-unused-vars
	function handleSort(column) {
		// No sorting needed for now
	}

	const buttonItems = [
		{
			label: 'Agregar usuario',
			onClick: () => newUserHandler(),
			icon: <FaPlus />,
			type: 'create',
		},
	];

	const tableHeaderProps = {
		buttons: buttonItems,
		icon: <FaUsers />,
		title: 'Usuarios',
		filterOptions: [],
		selectedFilterOption: '',
		openFilterHandler: handleDropdownToggle,
		onChangeFilter: () => {},
		getFilterUrl: getFilterUrl,
		navigate: navigate
	};

	const filterFormProps = {
		filterOptions: filterOptions,
		isOpen: isOpen,
		handleDropdownToggle: handleDropdownToggle,
		handleInputChange: handleInputChange,
		clearFilter: handleClearFilter,
		handleApplyFilter: handleApplyFilter
	};

	const dynamicListProps = {
		data: Array.isArray(users) ? users : [],
		loading: loading,
		error: error,
		count: Array.isArray(users) ? users.length : 0,
		onPageChange: () => {},
		page: 1,
		pageSize: 15,
		handleSort: handleSort,
		sortColumn: '',
		direction: 'asc',
		actionButtons: {
			seeDetails: seeDetailsHandler,
			editHandler: editUserHandler,
			deleteHandler: deleteUserHandler,
		},
		columnInfo: usersColumns,
		dataName: 'users',
		showCheckboxColumn: false,
		frontPaginator: true,
		posts: 15,
	};

	return (
		<div>
			<Helmet>
				<title>Lista de Usuarios</title>
			</Helmet>
			<div className='admin-con'>
				<ObjectTable
					headerProps={tableHeaderProps}
					filterProps={filterFormProps}
					tableProps={dynamicListProps}
				/>
			</div>
			<ConfirmationModal
				show={showDeleteModal}
				handleClose={handleCloseDeleteModal}
				handleConfirm={handleConfirmDelete}
				title={t('title.deleteUser')}
				description={t('msg.confirmDeleteUser')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>
		</div>
	);
}

export default AdminUsers;
